import styled from 'styled-components'
import { black, border1, danger, orange, surface2, textMuted, white } from '@/theme/colors'
import { fieldStyles, flatButton } from '@/theme/recipes'
import { layers, motion, radii, shadows } from '@/theme/tokens'
import { text } from '@/theme/typography'

/**
 * No width of its own: in a form group it fills the column like any field, in
 * a flex row it takes its content's width — a caller wanting more sets a
 * min-width on its wrapper.
 */
export const DropdownRoot = styled.div`
	position: relative;
`

/**
 * The closed control: a button on the field recipe, so it sits in a row of
 * inputs as one of them. Open, it takes the field's focus ring.
 */
export const DropdownTrigger = styled.button`
	${flatButton}
	${fieldStyles}
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding-right: 12px;
	text-align: left;

	/* The field ring is the focus ring here; no second outline. */
	&:focus-visible {
		outline: none;
	}

	&[aria-expanded='true'] {
		border-color: ${orange};
		box-shadow: ${shadows.focus};
	}

	&[aria-invalid='true'] {
		border-color: ${danger};
	}
`

export const DropdownValue = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const DropdownChevron = styled.span<{ $open: boolean }>`
	display: flex;
	flex-shrink: 0;
	color: ${textMuted};
	transition: transform ${motion.fast};
	transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`

/** Portal-positioned option list on the popover layer, like the places search and the currency picker. */
export const DropdownMenu = styled.div<{ $top: number; $left: number; $width: number }>`
	position: fixed;
	top: ${({ $top }) => $top}px;
	left: ${({ $left }) => $left}px;
	width: ${({ $width }) => $width}px;
	padding: 6px;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.overlay};
	max-height: 300px;
	overflow-y: auto;
	z-index: ${layers.popover};
`

/** One choice: the chosen one on the warm tint, the keyboard's or pointer's on the hover ground. */
export const DropdownOption = styled.div<{ $selected: boolean; $highlighted: boolean }>`
	padding: 10px 12px;
	border-radius: ${radii.small};
	cursor: pointer;
	${text.bodySm}
	color: ${black};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	transition: background-color ${motion.fast};
	background: ${({ $selected, $highlighted }) =>
		$selected ? `${orange}14` : $highlighted ? surface2 : 'transparent'};
`
